import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { getSurvey, getSurveyEnrichment } from '../modules/surveydata';
import Spinner from '../common/spinner';
// mui 5 only
import {
    Grid,
    Checkbox,
    Paper,
    Typography,
    Card,
    CardContent,
    Stack,
    Button,
    ThemeProvider,
    CircularProgress,
    TextField,
    Box,
} from '@mui/material';
import { mapFilterClass, mapMachineType, mapFilterType } from './surveyMapping';
import Footer from '../main/footer';
import FooterPadding from '../main/footerpadding';
import DownloadIcon from '@mui/icons-material/Download';
import { getInspectionPdf, downloadingPdf } from '../modules/surveydata';
import RichTextEditor from '../common/richtexteditor';
import vadoTheme from '../main/VadoThemeMui5'; // Import VadoTheme for mui 5
import ImageListAccordion from './imagelistaccordion';
import { useImageUrlCache } from './useImageUrlCache';
import { flatMapDeep, isObject, uniqBy, map, cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import PressureDiffLineGraph from './pressuredifflinegraph';
import VideoList from './videolist';
import PdfOptionDialog from '../common/pdfoptiondialog';

const generationSteps = {
    SELECT_MACHINES: 'SELECT_MACHINES',
    ACCEPT_DATA: 'ACCEPT_DATA',
};

export default function SurveyInspectionCreate(props) {
    const id = props.match.params.surveyId;
    const dispatch = useDispatch();
    const [survey, setSurvey] = useState(null);
    const [selectedMachines, setSelectedMachines] = useState([]);
    const [generationStep, setGenerationStep] = useState(generationSteps.SELECT_MACHINES);
    const [insepectionReport, setInspectionReport] = useState(null);
    const [machineNotes, setMachineNotes] = useState({});
    const [uniqImgIds, setUniqImgIds] = useState([]);
    const loadingPdf = useSelector((state) => state.surveydata.downloadingPdf);
    const fetchEditorContentRef = useRef(null);
    const [selectedImages, setSelectedImages] = useState([]);
    const [surveyEnrichment, setSurveyEnrichment] = useState(null);
    const { imageUrls } = useImageUrlCache(uniqImgIds, id, dispatch);
    const [selectedVideos, setSelectedVideos] = useState([]);
    const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        async function getSurveyData() {
            var resp = await getSurvey(id)(dispatch);
            // add uuid:s to machinRoom and machine level if missing
            const surveyWithIds = addMissingIdsToSurvey(resp);
            setSurvey(surveyWithIds);
        }
        async function getSurveyEnrichmentData() {
            var resp = await getSurveyEnrichment(id)(dispatch);
            setSurveyEnrichment(resp);
        }
        if (!survey) {
            getSurveyData();
            getSurveyEnrichmentData();
        }
    });

    const getAllSurveyPictures = useCallback((survey) => {
        return flatMapDeep(survey, (value, key) => {
            if (key === 'pictures' && Array.isArray(value)) return value;
            if (key === 'coverPicture' && isObject(value)) return [value];
            if (isObject(value)) return getAllSurveyPictures(value);
            return [];
        });
    }, []);

    useEffect(() => {
        if (!survey) return;
        const pictures = getAllSurveyPictures(survey);
        setUniqImgIds(uniqBy(map(pictures, 'imageId')));
    }, [survey, getAllSurveyPictures]);

    function generateUUID() {
        // RFC4122 version 4 compliant UUID
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0,
                v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    function addMissingIdsToSurvey(survey) {
        // Add id to machineRooms and machines if missing or falsy
        return {
            ...survey,
            machineRooms: (survey.machineRooms || []).map((room) => ({
                ...room,
                id: room.id ? room.id : generateUUID(),
                machines: (room.machines || []).map((machine) => ({
                    ...machine,
                    id: machine.id ? machine.id : generateUUID(),
                })),
            })),
        };
    }

    const onMachineSelect = (machineId) => {
        setSelectedMachines((prevSelected) =>
            prevSelected.includes(machineId)
                ? prevSelected.filter((key) => key !== machineId)
                : [...prevSelected, machineId]
        );
    };

    // muodostaa valinnan mukaisen konehuone tiedont. Jos konehuone ei sisällä yhtään valittua kotetta, tiputetaan se pois listalsta
    const generateNewSurveyModel = () => {
        const newSurvey = {
            ...survey,
            machineRooms: survey.machineRooms
                .map((machineRoom) => ({
                    ...machineRoom,
                    machines: machineRoom.machines.filter((machine) => selectedMachines.includes(machine.id)),
                }))
                .filter((machineRoom) => machineRoom.machines.length > 0), // Remove empty machineRooms
        };
        console.log(newSurvey);
        setInspectionReport(newSurvey);
    };

    const onNextStep = () => {
        switch (generationStep) {
            case generationSteps.SELECT_MACHINES:
                generateNewSurveyModel();
                setGenerationStep(generationSteps.ACCEPT_DATA);
                break;
            case generationSteps.ACCEPT_DATA:
                // Handle the next step
                break;
            default:
                break;
        }
    };

    const onCancel = () => {
        props.history.goBack();
    };

    const onDownloadSurvey = async (download, permanentStore) => {
        setPdfDialogOpen(false);
        // check if user cancelled the action
        if (!download) return;
        // get html editor content as base64
        const editorHtmlContent = fetchEditorContentRef.current();
        // grep the svg graphs in dom
        const svgs = Array.from(document.querySelectorAll('svg[id^="svg-chart-"]'));
        const svgDict = {};
        svgs.forEach((svg) => {
            const guid = svg.id.replace('svg-chart-', '');
            const base64 = Buffer.from(svg.outerHTML, 'utf-8').toString('base64');
            svgDict[guid] = base64;
        });
        dispatch(downloadingPdf());
        await getInspectionPdf(
            survey.id,
            insepectionReport,
            editorHtmlContent,
            machineNotes,
            selectedImages,
            svgDict,
            selectedVideos,
            permanentStore
        )(dispatch);
    };

    const onDownloadStart = () => {
        setPdfDialogOpen(true);
    };

    const onMachineNoteChange = (event, imageId) => {
        const value = event.target.value;

        setMachineNotes((prev) => ({
            ...prev,
            [imageId]: value,
        }));
    };

    const getEditorContentHtml = (fetchEditorContent) => {
        fetchEditorContentRef.current = fetchEditorContent;
    };

    const imageMarkedToPdf = (imageId) => {
        if (selectedImages.includes(imageId)) {
            return true;
        }
        return false;
    };

    const onImageMarkedToPdf = (imageId) => {
        let newSelectedImages = [...selectedImages];

        if (newSelectedImages.includes(imageId)) {
            newSelectedImages = newSelectedImages.filter((id) => id !== imageId);
        } else {
            newSelectedImages.push(imageId);
        }

        setSelectedImages(newSelectedImages);
    };

    const onImageCommentChange = (event, imageId) => {
        const newInspectionReport = cloneDeep(insepectionReport);
        const target = event.target;
        const value = target.value;

        // Search recursively for the imageId and update the comment
        const updatePictureComment = (node) => {
            if (!node) return false;

            // Current node
            if (node.pictures) {
                const picture = node.pictures.find((pic) => pic.imageId === imageId);
                if (picture) {
                    // Picture element found, update the comment
                    picture.comments = value;
                    return true;
                }
            }

            // Search from machine level
            if (node.machines) {
                for (const machine of node.machines) {
                    // if the picture is found in the machine, return true
                    if (updatePictureComment(machine)) return true;
                }
            }

            if (node.machineRooms) {
                for (const room of node.machineRooms) {
                    // if the picture is found in the machine room, return true
                    if (updatePictureComment(room)) return true;
                }
            }

            // not found, return false and continue search
            return false;
        };

        updatePictureComment(newInspectionReport);

        setInspectionReport(newInspectionReport);
    };

    const GeneratePressureDiffGraph = ({ machine }) => {
        if (
            machine.pressureDiffVado &&
            machine.pressureDiffVado.points &&
            machine.pressureDiffVado.points.length > 0 &&
            (!machine.pressureDiffCompetitor ||
                !machine.pressureDiffCompetitor.points ||
                machine.pressureDiffCompetitor.points.length === 0)
        ) {
            // only Vado data is present
            return (
                <PressureDiffLineGraph
                    id={machine.id}
                    labels={machine.pressureDiffVado.points.map((p) => p.month)}
                    seriesA={machine.pressureDiffVado.points.map((p) => p.pressureDiff)}
                    xAxisLabel="Kuukausi"
                    yAxisLabel="Pa"
                    seriesAName="Vado"
                />
            );
        } else if (
            // only competitor data is present
            machine.pressureDiffCompetitor &&
            machine.pressureDiffCompetitor.points &&
            machine.pressureDiffCompetitor.points.length > 0 &&
            (!machine.pressureDiffVado ||
                !machine.pressureDiffVado.points ||
                machine.pressureDiffVado.points.length === 0)
        ) {
            return (
                <PressureDiffLineGraph
                    id={machine.id}
                    labels={machine.pressureDiffCompetitor.points.map((p) => p.month)}
                    seriesB={machine.pressureDiffCompetitor.points.map((p) => p.pressureDiff)}
                    xAxisLabel="Kuukausi"
                    yAxisLabel="Pa"
                    seriesBName="Aikaisempi"
                />
            );
        }

        // combine vado and competitor months to labels and generate empty value to series if missing a labels point
        const allMonths = Array.from(
            new Set([
                ...machine.pressureDiffVado.points.map((p) => p.month),
                ...machine.pressureDiffCompetitor.points.map((p) => p.month),
            ])
        ).sort((a, b) => a - b);

        // Map values for each month, fill with '' if missing
        const vadoSeries = allMonths.map((month) => {
            const found = machine.pressureDiffVado.points.find((p) => p.month === month);
            return found ? found.pressureDiff : null;
        });

        const competitorSeries = allMonths.map((month) => {
            const found = machine.pressureDiffCompetitor.points.find((p) => p.month === month);
            return found ? found.pressureDiff : null;
        });

        return (
            <PressureDiffLineGraph
                id={machine.id}
                labels={allMonths}
                seriesA={vadoSeries}
                seriesB={competitorSeries}
                xAxisLabel="Kuukausi"
                yAxisLabel="Pa"
                seriesAName="Vado"
                seriesBName="Aikaisempi"
            />
        );
    };

    const hasPressureDiffData = (machine) => {
        return (
            (machine.pressureDiffVado &&
                machine.pressureDiffVado.points &&
                machine.pressureDiffVado.points.length > 0) ||
            (machine.pressureDiffCompetitor &&
                machine.pressureDiffCompetitor.points &&
                machine.pressureDiffCompetitor.points.length > 0)
        );
    };

    const onVideoCommentChange = (videoId, comments) => {
        const newInspectionReport = cloneDeep(insepectionReport);
        // update only machine level videos, other level is not supported
        newInspectionReport.machineRooms.forEach((machineRoom) => {
            machineRoom.machines.forEach((machine) => {
                const video = machine.videos.find((v) => v.videoId === videoId);
                if (video) {
                    video.comments = comments;
                }
            });
        });
        setInspectionReport(newInspectionReport);
    };

    const onVideoSelectChange = (videoId) => {
        setSelectedVideos((prevSelected) => {
            if (prevSelected.includes(videoId)) {
                return prevSelected.filter((id) => id !== videoId);
            } else {
                return [...prevSelected, videoId];
            }
        });
    };

    if (!survey || !surveyEnrichment) {
        return <Spinner />;
    }

    return (
        <ThemeProvider theme={vadoTheme}>
            <Paper
                elevation={0}
                sx={{
                    mb: 1,
                    backgroundColor: 'transparent',
                    p: 1,
                    transition: 'all 0.2s ease',
                }}>
                {generationStep === generationSteps.SELECT_MACHINES && (
                    <Grid container alignItems="center">
                        <Grid item xs={12}>
                            <p>
                                {t('survey.inspectionReportCreating', {
                                    surveyName: survey.name,
                                })}
                            </p>
                            <p>{t('survey.inspectionReportSelectMachines')}</p>
                        </Grid>
                        {survey.machineRooms.map((machineRoom) => (
                            <React.Fragment key={machineRoom.id}>
                                <Grid item xs={12}>
                                    <h3>{machineRoom.name}</h3>
                                </Grid>
                                {machineRoom.machines.length === 0 && (
                                    <Grid item xs={12}>
                                        {t('survey.noMachines')}
                                    </Grid>
                                )}
                                {machineRoom.machines.map((machine) => (
                                    <React.Fragment key={machine.id}>
                                        <Grid item xs={1}>
                                            <Checkbox
                                                checked={selectedMachines.includes(machine.id)}
                                                onChange={() => onMachineSelect(machine.id)}
                                            />
                                        </Grid>
                                        <Grid item xs={11}>
                                            <Card variant="outlined" sx={{ mb: 2, p: 1 }}>
                                                <CardContent>
                                                    <Typography variant="h6" gutterBottom>
                                                        {machine.name}
                                                    </Typography>
                                                    <Stack spacing={0.5}>
                                                        <Typography variant="body2">
                                                            {t('survey.machineType')}: {mapMachineType(machine.type)}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {t('location.airVolume')}: {machine.airVolume} m²
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {t('survey.filterClass')}:{' '}
                                                            {mapFilterClass(machine.filterClass)}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {t('survey.filterEfficiency')}: {machine.filterEfficiency} %
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {t('survey.filterType')}:{' '}
                                                            {mapFilterType(machine.filterType)}
                                                        </Typography>
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </React.Fragment>
                                ))}
                            </React.Fragment>
                        ))}
                        <Grid item xs={12}>
                            <Button
                                data-testid="btn-use-selected-machines"
                                size="small"
                                variant="contained"
                                color="secondary"
                                disabled={selectedMachines.length === 0}
                                onClick={onNextStep}>
                                {t('survey.useSelectedMachines')}
                            </Button>
                        </Grid>
                    </Grid>
                )}
                {generationStep === generationSteps.ACCEPT_DATA && (
                    <Grid container alignItems="center">
                        <Grid item xs={12}>
                            <h2>{t('survey.summaryInspection')}</h2>
                            <RichTextEditor
                                initialData={surveyEnrichment}
                                getEditorContentHtml={getEditorContentHtml}
                                viewOnly={false}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <h2>{t('location.machines')}</h2>
                        </Grid>
                        <Grid item xs={12}>
                            {insepectionReport.machineRooms.map((machineRoom) => (
                                <React.Fragment key={machineRoom.id}>
                                    <Grid item xs={12}>
                                        <h3>{machineRoom.name}</h3>
                                    </Grid>
                                    {/*machineRoom.pictures && machineRoom.pictures.length !== 0 && (
                                        <Grid item xs={12}>
                                            {'Konehuonekuvat'}
                                        </Grid>
                                    */}
                                    {machineRoom.machines.map((machine) => (
                                        <React.Fragment key={machine.id}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <Card variant="outlined" sx={{ mb: 2, p: 1 }}>
                                                        <CardContent>
                                                            <Typography variant="h6" gutterBottom>
                                                                {machine.name}
                                                            </Typography>
                                                            <Stack spacing={0.5}>
                                                                <Typography variant="body2">
                                                                    {t('survey.machineType')}:{' '}
                                                                    {mapMachineType(machine.type)}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {t('location.airVolume')}: {machine.airVolume} m²
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {t('survey.filterClass')}:{' '}
                                                                    {mapFilterClass(machine.filterClass)}
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {t('survey.filterEfficiency')}:{' '}
                                                                    {machine.filterEfficiency} %
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {t('survey.filterType')}:{' '}
                                                                    {mapFilterType(machine.filterType)}
                                                                </Typography>
                                                            </Stack>
                                                            <Box sx={{ mt: 2 }}>
                                                                <TextField
                                                                    data-testid={`machine-note-${machine.id}`}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    multiline
                                                                    maxRows={3}
                                                                    label={t('survey.observations')}
                                                                    defaultValue={''}
                                                                    name="machine-notes"
                                                                    onBlur={(e) => {
                                                                        onMachineNoteChange(e, machine.id);
                                                                    }}
                                                                    sx={{
                                                                        width: '100%',
                                                                        maxWidth: '600px',
                                                                    }}
                                                                />
                                                            </Box>
                                                            {hasPressureDiffData(machine) && (
                                                                <Box sx={{ mt: 2 }}>
                                                                    <Typography variant="h6">
                                                                        Paine-ero kaaviona
                                                                    </Typography>
                                                                    <GeneratePressureDiffGraph machine={machine} />
                                                                </Box>
                                                            )}

                                                            {machine.videos && machine.videos.length !== 0 && (
                                                                <Box sx={{ mt: 2 }}>
                                                                    <Grid item xs={12}>
                                                                        {'Konevideot'}
                                                                    </Grid>
                                                                    <Grid item xs={12}>
                                                                        <VideoList
                                                                            videos={machine.videos}
                                                                            editMode={true}
                                                                            selectedVideos={selectedVideos}
                                                                            videoCommentChange={onVideoCommentChange}
                                                                            videoSelectChange={onVideoSelectChange}
                                                                        />
                                                                    </Grid>
                                                                </Box>
                                                            )}

                                                            {machine.pictures && machine.pictures.length !== 0 && (
                                                                <Box sx={{ mt: 2 }}>
                                                                    <Grid item xs={12}>
                                                                        {'Konekuvat'}
                                                                    </Grid>
                                                                </Box>
                                                            )}
                                                            <ImageListAccordion
                                                                pictures={machine.pictures}
                                                                imageUrls={imageUrls}
                                                                imageMarkedToPdf={imageMarkedToPdf}
                                                                onImageMarkedToPdf={onImageMarkedToPdf}
                                                                onImageCommentChange={onImageCommentChange}
                                                            />
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            </Grid>
                                        </React.Fragment>
                                    ))}
                                </React.Fragment>
                            ))}
                        </Grid>
                    </Grid>
                )}
            </Paper>
            <PdfOptionDialog open={pdfDialogOpen} onClose={onDownloadSurvey} />
            <Footer>
                <Grid container spacing={2} justifyContent="center" alignItems="center">
                    <Grid item>
                        <Button
                            data-testid="btn-survey-pdf"
                            variant="contained"
                            color="primary"
                            name="btn-survey-pdf"
                            onClick={onDownloadStart}
                            startIcon={<DownloadIcon />}
                            disabled={generationStep !== generationSteps.ACCEPT_DATA || loadingPdf}>
                            {loadingPdf ? <CircularProgress size={24} color="inherit" /> : 'PDF'}
                        </Button>
                    </Grid>

                    <Grid item>
                        <Button variant="contained" color="primary" name="sulje" onClick={onCancel}>
                            <i className="fas fa-window-close"></i>&nbsp;{t('buttons.close')}
                        </Button>
                    </Grid>
                </Grid>
            </Footer>
            <FooterPadding />
        </ThemeProvider>
    );
}

SurveyInspectionCreate.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            surveyId: PropTypes.string.isRequired,
        }).isRequired,
    }).isRequired,
    history: PropTypes.object.isRequired,
};
